// Utilities for persisting a referral code (?ref=CODE) across page
// navigations and the Google OAuth round-trip, so a click on an invite
// email's link pre-fills the referral input at paid checkout (REF-8).
//
// Storage strategy:
//   Primary:  sessionStorage — fast, tab-scoped.
//   Fallback: cookie (SameSite=Lax) — survives the accounts.google.com
//             redirect even when sessionStorage isn't readable after the
//             round-trip (some mobile browsers).
//
// Key names + TTL are pinned to what REF-8 (#515) shipped: existing
// users with a `ref_code` cookie from this week must continue to get a
// pre-filled checkout form after this refactor.

const SS_KEY = 'referral_code'
const COOKIE_KEY = 'ref_code'
const COOKIE_MAX_AGE_S = 7 * 24 * 60 * 60

/**
 * Persist a referral code in sessionStorage and as a cookie.
 * No-op on the server and for empty input.
 */
export function storeRefCode(code: string): void {
  if (typeof window === 'undefined' || !code) return
  try {
    sessionStorage.setItem(SS_KEY, code)
  } catch {
    // Private browsing can block sessionStorage writes — continue to cookie.
  }
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(code)}; Path=/; Max-Age=${COOKIE_MAX_AGE_S}; SameSite=Lax`
}

/**
 * Read the previously stored referral code, or '' if none.
 * sessionStorage wins over cookie — they should agree, but the
 * same-tab signal is the most trustworthy when both exist.
 */
export function getStoredRefCode(): string {
  if (typeof window === 'undefined') return ''
  try {
    const ss = sessionStorage.getItem(SS_KEY)
    if (ss) return ss
  } catch {
    // Fall through to cookie.
  }
  const match = document.cookie.match(/(?:^|;\s*)ref_code=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : ''
}

/**
 * Clear the stored referral code (e.g. after it has been redeemed).
 */
export function clearRefCode(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(SS_KEY)
  } catch {
    // ignore
  }
  document.cookie = `${COOKIE_KEY}=; Path=/; Max-Age=0; SameSite=Lax`
}
